import { DateTime } from 'luxon';

export function pluralize(
  count: number,
  singular: string,
  plural: string,
  shouldShowCount = true,
): string {
  const num = Number(count) || 0;
  const singularStr = singular || '';
  const pluralStr = plural || '';
  const noun = `${num === 1 ? singularStr : pluralStr}`;

  return shouldShowCount ? `${num} ${noun}` : noun;
}

/** Return str with the first character of each word to upper case. */
export function capitalizeWords(str: string | unknown): string {
  if (!str) return '';
  const stringValue = String(str);
  if (stringValue.length === 0) {
    return stringValue;
  }

  return stringValue
    .split(' ')
    .map((word) => word[0]?.toUpperCase() + word.slice(1))
    .join(' ');
}

/** Return str with the first character of the entire string in upper case, and the rest lower case.
 *  e.g. "let's GO to the beach" -> "Let's go to the beach"
 */
export function capitalizeSentence(str: string | unknown | undefined): string {
  if (!str) {
    return '';
  }

  const stringValue = String(str);
  if (stringValue.length === 0) {
    return stringValue;
  }

  return stringValue[0].toUpperCase() + stringValue.slice(1).toLowerCase();
}

/** Return str with the first name from a fullName string
 *  e.g. "Michelle Smith" -> "Michelle"
 *  e.g. "Jose Alvarez Rodriguez" -> "Jose Alvarez"
 */
export const getFirstName = (fullName: string | unknown) => {
  if (!fullName) return '';
  const names = capitalizeWords(String(fullName)).split(' ').filter(Boolean);
  if (names.length === 1) {
    return names[0];
  }
  return names.slice(0, names.length - 1).join(' ');
};

/** Return str with the last name from a fullName string
 *  e.g. "Michelle Smith" -> "Smith"
 *  e.g. "Jose Alvarez Rodriguez" -> "Rodriguez"
 */
export const getLastName = (fullName: string | unknown) => {
  if (!fullName) return '';
  const names = capitalizeWords(String(fullName)).split(' ').filter(Boolean);
  if (names.length < 2) return '';
  return names[names.length - 1];
};

/** Return names array to sentence
 *  e.g. ["Rick Doblin", "Mike Tyson", "Terrence McKenna"] -> "Rick Doblin, Mike Tyson, and Terrence McKenna"
 */
export const namesToOxfordCommaList = (names: (string | unknown)[]) => {
  if (!names || !Array.isArray(names) || names.length === 0) return '';
  if (names.length === 1) return String(names[0]);
  const last = String(names.pop());
  return names.map(String).join(', ') + ' and ' + last;
};

export const NUMBER_TO_WORD = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
  'twenty',
];

/**
 * Return clock time for given dateObj in standard digital clock format by default
 */

export function displayTime(date: DateTime, dateFormat = 'hh:mm a'): string {
  return date.toFormat(dateFormat).toLowerCase();
}

export const removeFileExtension = (file: File | undefined) => {
  return file?.name.split('.')[0] ?? '';
};

export function formatOxfordCommaList(items: (string | unknown)[]): string {
  if (!items || !Array.isArray(items) || items.length === 0) {
    return '';
  }
  if (items.length === 1) {
    return String(items[0]);
  }
  if (items.length === 2) {
    return `${String(items[0])} and ${String(items[1])}`;
  }

  const lastItem = String(items[items.length - 1]);
  const otherItems = items.slice(0, -1).map(String);
  return `${otherItems.join(', ')}, and ${lastItem}`;
}
